/**
 * Google Ads conversion tracking
 */

import type { AnalyticsUser, ConversionConfig } from './types';

// Ensure gtag is available on window and returns a callable function
function ensureGtag(): (...args: unknown[]) => void {
  const w = window as typeof window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer!.push(args);
    };
  }
  return w.gtag;
}

/**
 * Upgrade ad consent from the denied defaults set in index.html.
 *
 * This - not the absence of our AW- tag - is what actually keeps machine traffic
 * out of ad audiences: the GA4 tag is linked to the Ads account and fires the Ads
 * endpoints itself on load, so the only way to hold them back is to deny
 * ad_storage until we have evidence of a human. Verified in a browser: 8 ad
 * requests with the defaults absent, 0 with them present, GA4 unaffected in both.
 *
 * Idempotent from gtag's side - a repeat grant is a no-op update.
 */
export function grantAdConsent(): void {
  const gtag = ensureGtag();
  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
}

/**
 * Initialize Google Ads tracking: load the tag library, then configure it.
 *
 * The <script> used to live in index.html, but merely loading gtag/js for an AW-
 * id configures that destination and fires a page_view to google.com/ccm/collect
 * plus ad.doubleclick.net cookie-matching - before any app code runs. That put
 * every mail-scanner fetch of an emailed link into our ad audiences and
 * view-through bidding (CODEPULSE-BACKEND-2ZY). Injecting it here puts it behind
 * the caller's gate: first human interaction, or a conversion.
 *
 * The GA4 tag stays in index.html - GA4 should see all traffic.
 */
export function init(conversionId: string): void {
  const gtag = ensureGtag();

  // Idempotent: analytics.initAdNetworks() already guards, but a stray second
  // call must not load a second copy of the library.
  const src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
  if (!document.querySelector(`script[src="${src}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }

  // Queues in dataLayer until the library above is ready, so ordering is safe.
  gtag('config', conversionId);
}

/**
 * Hash email using SHA-256 for enhanced conversions
 */
export async function hashEmail(email: string): Promise<string> {
  const normalizedEmail = email.toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalizedEmail);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Set user data for enhanced conversions
 * This improves conversion attribution accuracy
 */
export async function setUserData(user: AnalyticsUser): Promise<void> {
  if (!user.email) return;

  const hashedEmail = await hashEmail(user.email);
  const gtag = ensureGtag();

  gtag('set', 'user_data', {
    sha256_email_address: hashedEmail,
  });
}

/**
 * Track a conversion event
 */
export function trackConversion(
  config: ConversionConfig,
  value?: number,
  currency?: string,
  transactionId?: string
): void {
  const gtag = ensureGtag();
  const conversionParams: Record<string, unknown> = {
    send_to: `${config.id}/${config.label}`,
  };

  if (value !== undefined) {
    conversionParams.value = value;
    conversionParams.currency = currency || 'USD';
  }

  if (transactionId) {
    conversionParams.transaction_id = transactionId;
  }

  gtag('event', 'conversion', conversionParams);
}

/**
 * Track sign-up conversion
 */
export function trackSignUpConversion(config: ConversionConfig): void {
  trackConversion(config);
}

/**
 * Track purchase conversion with value
 */
export function trackPurchaseConversion(
  config: ConversionConfig,
  value: number,
  currency: string,
  transactionId?: string
): void {
  trackConversion(config, value, currency, transactionId);
}

/**
 * Track trial started conversion
 */
export function trackTrialConversion(config: ConversionConfig): void {
  trackConversion(config);
}
